import { Module, MutationTree, ActionTree, GetterTree } from 'vuex'

interface BookingItem {
  id: string
  serviceType: string
  pickupDate: string
  returnDate?: string
  pickupLocation: string
  destination?: string
  passengers: number
  carType?: string
  driverRequired: boolean
  notes?: string
  price: number
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled'
  createdAt: string
}

interface BookingState {
  bookings: BookingItem[]
  currentBooking: Partial<BookingItem> | null
  totalBookings: number
}

const state: BookingState = {
  bookings: [],
  currentBooking: null,
  totalBookings: 0
}

const getters: GetterTree<BookingState, any> = {
  allBookings: (state) => state.bookings,
  currentBooking: (state) => state.currentBooking,
  bookingCount: (state) => state.bookings.length,
  confirmedBookings: (state) => state.bookings.filter(b => b.status === 'confirmed'),
  pendingBookings: (state) => state.bookings.filter(b => b.status === 'pending'),
  getBookingById: (state) => (id: string) => state.bookings.find(b => b.id === id)
}

const mutations: MutationTree<BookingState> = {
  ADD_BOOKING: (state, booking: BookingItem) => {
    state.bookings.push(booking)
    state.totalBookings = state.bookings.length
  },
  UPDATE_BOOKING: (state, updatedBooking: BookingItem) => {
    const index = state.bookings.findIndex(b => b.id === updatedBooking.id)
    if (index !== -1) {
      state.bookings[index] = updatedBooking
    }
  },
  DELETE_BOOKING: (state, bookingId: string) => {
    state.bookings = state.bookings.filter(b => b.id !== bookingId)
    state.totalBookings = state.bookings.length
  },
  SET_CURRENT_BOOKING: (state, booking: Partial<BookingItem> | null) => {
    state.currentBooking = booking
  },
  CLEAR_BOOKINGS: (state) => {
    state.bookings = []
    state.currentBooking = null
    state.totalBookings = 0
  },
  SET_BOOKING_STATUS: (state, payload: { id: string; status: string }) => {
    const booking = state.bookings.find(b => b.id === payload.id)
    if (booking) {
      booking.status = payload.status as BookingItem['status']
    }
  }
}

const actions: ActionTree<BookingState, any> = {
  createBooking ({ commit }, booking: BookingItem) {
    commit('ADD_BOOKING', booking)
    commit('SET_CURRENT_BOOKING', booking)
    return booking
  },
  updateBooking ({ commit }, booking: BookingItem) {
    commit('UPDATE_BOOKING', booking)
    commit('SET_CURRENT_BOOKING', booking)
  },
  deleteBooking ({ commit }, bookingId: string) {
    commit('DELETE_BOOKING', bookingId)
  },
  setCurrentBooking ({ commit }, booking: Partial<BookingItem> | null) {
    commit('SET_CURRENT_BOOKING', booking)
  },
  updateBookingStatus ({ commit }, payload: { id: string; status: string }) {
    commit('SET_BOOKING_STATUS', payload)
  },
  clearAllBookings ({ commit }) {
    commit('CLEAR_BOOKINGS')
  }
}

const bookingModule: Module<BookingState, any> = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}

export default bookingModule
